import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { forwardRef, useRef } from 'react';
import { useButton } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { Glyph } from '../common/Glyph';

import { cssUtil } from '../../theme/cssUtil';
import { su } from '../../theme/functions/su';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

import { Button } from './Button';

export const OutlineButton = forwardRef((props, forwardedRef) => {
  const ref = useRef();
  const button = useButton(props, ref);

  return (
    <OutlineButton.Root
      {...button.buttonProps}
      ref={mergeRefs([ref, forwardedRef])}
      title={props.title}
      className={props.className}
      form={props.form}
      style={props.style}
      data-style-width={props.styleWidth}
      data-style-compact={props.styleCompact}
      data-style-icon-color-disabled={props.styleIconColorDisabled}
      data-is-pressed={button.isPressed}
      data-is-disabled={props.isDisabled}
    >
      {props.children}
    </OutlineButton.Root>
  );
});

OutlineButton.propTypes = {
  ...Button.propTypes,
  styleCompact: PropTypes.bool,
  styleWidth: PropTypes.oneOf(['full']),
};

OutlineButton.Text = styled.span`
  white-space: nowrap;
`;

OutlineButton.Icon = Glyph;

OutlineButton.Root = styled.button`
  --outline-button-border-color: ${theme.color.line};
  --outline-button-border-color-hover: ${theme.color.line_hover};
  --outline-button-font-color: ${theme.color.text};
  --outline-button-background-color: transparent;
  --outline-button-background-color-hover: ${theme.color.background_hover};

  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  padding: ${su(0.5)} ${su(2)};
  min-height: ${su(5)};
  background: var(--outline-button-background-color);
  color: var(--outline-button-font-color);
  // border: 1px dashed var(--outline-button-border-color);
  transition: ${motion.duration.fast} ${motion.curve.fast_in};
  transition-property: transform, color, background-color;

  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23DADAE2' stroke-width='3' stroke-dasharray='5' stroke-dashoffset='0' stroke-linejoin='miter' stroke-linecap='butt'/%3e%3c/svg%3e");

  ${cssUtil.isDarkTheme} {
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23292A2F' stroke-width='3' stroke-dasharray='5' stroke-dashoffset='0' stroke-linejoin='miter' stroke-linecap='butt'/%3e%3c/svg%3e");
  }

  border-radius: ${theme.borderRadius.default};

  &:not([data-style-icon-color-disabled='true']) {
    .${glyphStyles.root} {
      color: var(--outline-button-font-color);
    }
  }

  // States
  &:not([data-is-disabled='true']) {
    &:hover {
      border-color: var(--outline-button-border-color-hover);
      background-color: var(--outline-button-background-color-hover);
    }

    &[data-is-pressed='true'] {
      transform: scale(0.96875);
    }
  }

  &[data-is-disabled='true'] {
    --outline-button-background-color: ${theme.color.text_disabled};
    cursor: not-allowed;
  }

  // Size variants
  &[data-style-compact='true'] {
    grid-gap: 6px;
    padding-inline-start: ${su(1.5)};
    padding-inline-end: ${su(1.5)};
  }

  // Width variants
  &[data-style-width='full'] {
    width: 100%;
  }
`;
